'use client'

import { setupActionEndedEvent, setupActionFailedEvent, setupActionSuccessEvent } from '../../helpers/setupCustomEvents'
import { HandledApplicationError } from '../../errors/HandledApplicationError'
import { useEventDispatchQueue } from '../UseEventDispatchQueue'
import { ACTION_ENDED_EVENT, ACTION_FAILED_EVENT, ACTION_SUCCESS_EVENT } from '../../constants/custom-event-names'
import { DissectedError, DissectedErrorGroups } from '../../errors/DissectedError'
import { useActionState } from 'react'
import { deserializeError } from '../../errors/helpers/deserializeError'
import { wrapAction } from './actionWrapper'

export enum ActionType {
    CLIENT = 'client',
    SERVER = 'server',
}

export type Action<TState> = (state: TState, form: FormData) => Promise<TState>
export type ActionObject<TState> = {
    action: Action<TState>
    type: ActionType
}

const constructFormElementNotFoundError = (error: HandledApplicationError) => {
    return new DissectedError('no formElement found to dispatch the HandledApplicationError on', {
        cause: error,
        errorGroup: DissectedErrorGroups.UNEXPECTED_ERROR_PARSING_RESPONSE_OBJECT,
        verboseMessage: [
            'the extendedAction inside of the useAction hook',
            'could not find the formElement that called the action',
        ].join(' '),
    })
}

/**
 * @description
 * A hook that can be used to handle form submission through form actions
 * It takes an action, and returns a dynamic state that will be updated each time the action is resolved
 * It also returns a formAction that should be used in the form attribute of a form element
 *
 * the provided action will be extended and awaited, before giving it back to the application
 * after the promise resolves or throws errors, events are dispatched on the form element bubbling up
 * to the document. If no form element could be found, an Action ended event is send to the document
 *
 * @fires ActionFailed
 * @fires ActionSuccess
 * @fires ActionEnded
 *
 * @param action - an async (server)action that takes the current state and a FormData object,
 * and returns a new state on successful response
 * @param initialState - the initial state
 *
 * @returns object.state - the state that will be updated by the return value of the action, each
 * time it resolves.
 * @returns object.formAction - the formAction that should be used in the action attribute of
 * the form element
 * */
export const useAction = <TState>(action: ActionObject<TState>, initialState: TState, formId: string) => {
    const eventQueue = useEventDispatchQueue()

    const extendedAction: Action<TState> = async (state: TState, form: FormData) => {
        const formElement = document.getElementById(formId) as HTMLFormElement | null
        let actionEventSent = false
        try {
            let actionResult: string | Awaited<TState> | undefined = undefined

            if (action.type === ActionType.SERVER) {
                actionResult = await wrapAction(action.action, state, form, formId)
            } else {
                actionResult = await action.action(state, form)
            }
            /** if the result is a string, it could be one of these situations:
             ** 1. the result from the action was a string
             ** 2. the action went awry and the result is a SerializedError */
            if (typeof actionResult === 'string') {
                const deserializedError = deserializeError(actionResult)
                if (
                    deserializedError instanceof DissectedError ||
                    deserializedError instanceof HandledApplicationError
                ) {
                    throw deserializedError
                }
            }

            const newState = actionResult as Awaited<TState>

            if (!formElement) return newState

            eventQueue.queueEvent(
                `${formId}-${ACTION_SUCCESS_EVENT}`,
                setupActionSuccessEvent(formId, newState),
                formElement,
                0,
            )

            actionEventSent = true

            return newState
        } catch (error) {
            if (!(error instanceof HandledApplicationError)) throw error
            if (!formElement) throw constructFormElementNotFoundError(error)

            eventQueue.queueEvent(
                `${formId}-${ACTION_FAILED_EVENT}`,
                setupActionFailedEvent(formId, error),
                formElement,
                0,
            )

            actionEventSent = true

            return state
        } finally {
            if (!actionEventSent)
                eventQueue.queueEvent(`${formId}=${ACTION_ENDED_EVENT}`, setupActionEndedEvent(formId), document, 0)
            actionEventSent = true
        }
    }

    const [actionState, formAction] = useActionState(extendedAction, initialState as Awaited<TState>)

    return {
        state: actionState,
        formAction,
    }
}
